import { Avatar, Button, Skeleton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { NetworkStatus, useQuery } from '@apollo/client';
import { enqueueSnackbar } from 'notistack';
import { DashSourceGroup, PlanLimit } from '@/graphql/generated/types.generated';
import { DashSourceProfilesDocument } from '@/graphql/generated/dash-source-profiles.generated';
import { LockOutlined, PortraitRounded } from '@mui/icons-material';
import ProfileAvatar from '../../dashboards/profiles/ProfileAvatar';
import DashboardSectionCard from '../DashboardSectionCard';
import DashboardEmptyState from '../DashboardEmptyState';
import AddCompetitor from '../../dashboards/profiles/AddCompetitor';
import DeleteCompetitorButton from '../../dashboards/profiles/DeleteCompetitorButton';
import LoadMore from '../../LoadMore';
import { PlanLimitsDocument } from '@/graphql/generated/plan-limits.generated';
import { Link } from 'react-router-dom';

const LIMIT = 8;

const DashboardCompetitorsList = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isCompact = useMediaQuery(theme.breakpoints.down('sm'));

    const { data: planLimitsData, loading: planLimitsLoading } = useQuery(PlanLimitsDocument, {
        variables: { planLimits: [PlanLimit.CompetitorsProfiles] },
    });

    const competitorsLimit = planLimitsData?.planLimits[0];
    const featureUnavailable = !planLimitsLoading && competitorsLimit?.limit === '0';

    const { loading, error, data, fetchMore, networkStatus, refetch } = useQuery(DashSourceProfilesDocument, {
        variables: {
            dashSourceGroup: DashSourceGroup.Competitors,
            take: LIMIT,
            skip: 0,
        },
        skip: planLimitsLoading || featureUnavailable,
        onError: (e) => enqueueSnackbar(e.message),
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    const refetching = loading && networkStatus === NetworkStatus.refetch;
    const initialLoad = (loading || !!error) && !refetching;

    const competitors = data?.dashSourceProfiles.items || [];
    const hasMore = data?.dashSourceProfiles.hasMore || false;
    const isEmpty = !initialLoad && competitors.length === 0;

    return (
        <DashboardSectionCard
            title={t('dashboard.competitors.title')}
            actionSlot={
                !featureUnavailable && (
                    <AddCompetitor compact={isCompact} onClose={(refreshed) => refreshed && refetch()} />
                )
            }
        >
            <Stack spacing={2} maxWidth="100%" flex={1} maxHeight={320} overflow="auto">
                {/* Feature unavailable — upgrade prompt */}
                {featureUnavailable && (
                    <DashboardEmptyState
                        icon={LockOutlined}
                        tone="warning"
                        title={t('dashboard.competitors.unavailable.title')}
                        description={t('dashboard.competitors.unavailable.support-text')}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            sx={{ borderRadius: 999 }}
                            component={Link}
                            to="/settings/workspace/billing#upgrade"
                        >
                            {t('dashboard.competitors.unavailable.cta')}
                        </Button>
                    </DashboardEmptyState>
                )}

                {/* Loading skeleton */}
                {!featureUnavailable && initialLoad && (
                    <Stack spacing={0.5}>
                        {[...Array(4)].map((_, i) => (
                            <Stack
                                key={i}
                                direction="row"
                                gap={1}
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1.5,
                                    bgcolor: 'background.paper-elevation-1',
                                }}
                            >
                                <Stack direction="row" gap={1} alignItems="center" minWidth={0} flex={1}>
                                    <Skeleton
                                        variant="rounded"
                                        width={theme.spacing(4)}
                                        height={theme.spacing(4)}
                                        sx={{ flexShrink: 0 }}
                                    />
                                    <Stack direction="column" minWidth={0}>
                                        <Stack direction="row" gap={0.5}>
                                            <Skeleton
                                                variant="circular"
                                                width={18}
                                                height={18}
                                                sx={{ flexShrink: 0 }}
                                            />
                                            <Skeleton variant="rounded" width={100} height={18} />
                                        </Stack>
                                        <Skeleton variant="rounded" width={70} height={14} sx={{ mt: 0.5 }} />
                                    </Stack>
                                </Stack>
                                <Skeleton
                                    variant="rounded"
                                    width={32}
                                    height={32}
                                    sx={{ borderRadius: '50%' }}
                                />
                            </Stack>
                        ))}
                    </Stack>
                )}

                {/* Empty state */}
                {!featureUnavailable && isEmpty && (
                    <DashboardEmptyState
                        icon={PortraitRounded}
                        tone="secondary"
                        title={t('dashboard.competitors.empty.title')}
                        description={t('dashboard.competitors.empty.support-text')}
                    />
                )}

                {/* Competitor rows */}
                {!featureUnavailable && !initialLoad && competitors.length > 0 && (
                    <Stack spacing={0.5}>
                        {competitors.map((competitor) => (
                            <Stack
                                key={competitor.id}
                                direction="row"
                                gap={1}
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 1.5,
                                    bgcolor: 'background.paper-elevation-1',
                                    transition: 'background-color 0.15s',
                                    '&:hover': { bgcolor: 'background.paper-elevation-2' },
                                }}
                            >
                                <Stack
                                    direction="row"
                                    gap={1}
                                    alignItems="center"
                                    minWidth={0}
                                    flex={1}
                                    overflow="hidden"
                                >
                                    <ProfileAvatar
                                        sx={{
                                            width: theme.spacing(4),
                                            height: theme.spacing(4),
                                            fontSize: theme.spacing(1.5),
                                            flexShrink: 0,
                                        }}
                                        name={competitor.name}
                                        picture={competitor.picture || undefined}
                                    />
                                    <Stack direction="column" minWidth={0} overflow="hidden">
                                        <Stack direction="row" gap={0.5} alignItems="center" minWidth={0}>
                                            <Avatar
                                                variant="square"
                                                src={`/social/${competitor.dashSource.code.toLowerCase()}.svg`}
                                                sx={{ width: 18, height: 18, flexShrink: 0 }}
                                                alt={competitor.dashSource.nameKey}
                                            />
                                            <Typography variant="body2" noWrap>
                                                {competitor.name}
                                            </Typography>
                                        </Stack>
                                        {competitor.username && (
                                            <Typography variant="caption" color="textDisabled" noWrap>
                                                @{competitor.username}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                                <DeleteCompetitorButton competitor={competitor} />
                            </Stack>
                        ))}

                        {/* Load more */}
                        {hasMore && (
                            <LoadMore
                                loadMore={() => {
                                    fetchMore({ variables: { skip: competitors.length } });
                                }}
                            />
                        )}
                    </Stack>
                )}
            </Stack>
        </DashboardSectionCard>
    );
};

export default DashboardCompetitorsList;
